import { useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../context/AuthContext";
import { errText, api } from "../lib/api";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Clock } from "lucide-react";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 13.2 4.5 4.5 13.2 4.5 24S13.2 43.5 24 43.5 43.5 34.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.5 29.5 4.5 24 4.5 16.3 4.5 9.7 8.8 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 43.5c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.2 34.6 26.7 35.5 24 35.5c-5.3 0-9.7-3.1-11.3-7.5l-6.5 5C9.6 39.1 16.2 43.5 24 43.5z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3C41 36.6 43.5 30.8 43.5 24c0-1.2-.1-2.3-.4-3.5z"/>
  </svg>
);

export const AuthModal = () => {
  const { authOpen, setAuthOpen, login, verify2FA, register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [seasonal, setSeasonal] = useState(true);
  const [newsletter, setNewsletter] = useState(false);
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [twoFA, setTwoFA] = useState(null); // { challenge_id, email_hint }
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(false);
  const [appeal, setAppeal] = useState(false);
  const [appealMsg, setAppealMsg] = useState("");
  const [appealInfo, setAppealInfo] = useState(null); // { loading, appeal }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const res = await login(email, password);
        if (res && res.two_fa_required) {
          setTwoFA({ challenge_id: res.challenge_id, email_hint: res.email_hint });
          toast.success("We emailed you a 6-digit login code");
          return;
        }
      } else {
        await register(name, email, password, seasonal);
        if (newsletter) await api.post("/me/newsletter", { join: true }).catch(() => {});
      }
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      setAuthOpen(false);
      setPassword("");
    } catch (err) {
      // Suspended accounts return 403 with a suspension message — show appeal + its status.
      const detail = (err?.response?.data?.detail || "").toLowerCase();
      if (mode === "login" && err?.response?.status === 403 && detail.includes("suspend")) {
        setAppeal(true);
        setAppealInfo({ loading: true });
        try {
          const { data } = await api.post("/auth/appeal/status", { email, password });
          setAppealInfo({ loading: false, appeal: data.appeal });
        } catch {
          setAppealInfo({ loading: false, appeal: null });
        }
        toast.error(errText(err));
        return;
      }
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  const submitAppeal = async (e) => {
    e.preventDefault();
    if (appealMsg.trim().length < 10) return toast.error("Please describe your appeal (at least 10 characters).");
    setLoading(true);
    try {
      await api.post("/auth/appeal", { email, password, message: appealMsg.trim() });
      toast.success("Appeal submitted. Our team will review it and reach out.");
      setAppealMsg("");
      setAppealInfo({ loading: false, appeal: { status: "open", created_at: new Date().toISOString() } });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  const closeAppeal = () => { setAppeal(false); setAppealMsg(""); setAppealInfo(null); };

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify2FA(twoFA.challenge_id, code, rememberDevice);
      toast.success("Welcome back!");
      setAuthOpen(false);
      setTwoFA(null); setCode(""); setRememberDevice(false); setPassword("");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  const demo = () => {
    setMode("login");
    setEmail("sarah@critterposts.ca");
    setPassword("Demo123!");
  };

  return (
    <Dialog open={authOpen} onOpenChange={setAuthOpen}>
      <DialogContent className="sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <img src="/favicon.png" alt="CritterPosts.ca" className="h-9 w-9 object-contain" data-testid="auth-logo" />
            <DialogTitle className="font-display text-xl text-[#12234A]">
              {mode === "login" ? "Welcome back" : "Join CritterPosts.ca"}
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-slate-500">
            {mode === "login" ? "Sign in to post ads, save favorites and message sellers." : "Create a free account in seconds."}
          </DialogDescription>
        </DialogHeader>

        {twoFA ? (
          <form onSubmit={submitCode} className="space-y-4" data-testid="twofa-form">            <div className="flex items-center gap-2 text-sm text-slate-500 bg-[#EEF4FF] rounded-xl px-3 py-2">
              <ShieldCheck size={16} className="text-[#12234A]" /> Enter the 6-digit code we emailed to <strong>{twoFA.email_hint}</strong>
            </div>
            <div>
              <Label htmlFor="twofa-code">Login code</Label>
              <Input id="twofa-code" data-testid="twofa-code" inputMode="numeric" maxLength={6} value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                className="mt-1 h-12 rounded-xl text-center text-2xl tracking-[0.4em] font-bold" placeholder="000000" autoFocus />
            </div>
            <Button type="submit" disabled={loading || code.length < 6} data-testid="twofa-submit" className="w-full h-11 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Verify & sign in"}
            </Button>
            <label className="flex items-center gap-2 text-sm text-slate-500 cursor-pointer select-none">
              <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} data-testid="twofa-remember" className="h-4 w-4 rounded border-slate-300 accent-[#F5551E]" />
              Don't ask on this device for 30 days
            </label>
            <button type="button" onClick={() => { setTwoFA(null); setCode(""); setRememberDevice(false); }} data-testid="twofa-cancel" className="w-full text-center text-sm text-slate-400 hover:text-slate-600">
              Back to sign in
            </button>
          </form>
        ) : appeal ? (
          appealInfo?.loading ? (
            <div className="py-10 flex justify-center" data-testid="appeal-loading"><Loader2 className="animate-spin text-slate-300" size={28} /></div>
          ) : appealInfo?.appeal?.status === "open" ? (
            <div className="space-y-4" data-testid="appeal-status-open">
              <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-xl px-3 py-3">
                <Clock size={18} className="text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold">Your appeal is under review</p>
                  <p className="text-amber-700/80 mt-0.5">Our team has your appeal{appealInfo.appeal.created_at ? ` from ${new Date(appealInfo.appeal.created_at).toLocaleDateString()}` : ""} and will reach out by email. You can't sign in until it's approved.</p>
                </div>
              </div>
              <button type="button" onClick={closeAppeal} data-testid="appeal-cancel" className="w-full text-center text-sm text-slate-400 hover:text-slate-600">Back to sign in</button>
            </div>
          ) : (
            <form onSubmit={submitAppeal} className="space-y-4" data-testid="appeal-form">
              {appealInfo?.appeal?.status === "rejected" ? (
                <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-xl px-3 py-3" data-testid="appeal-status-rejected">
                  <ShieldCheck size={18} className="text-red-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-semibold">Your previous appeal was declined</p>
                    {appealInfo.appeal.resolution_note && <p className="text-red-600/80 mt-0.5">Note from our team: "{appealInfo.appeal.resolution_note}"</p>}
                    <p className="text-red-600/80 mt-0.5">You can submit a new appeal with more details below.</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2.5">
                  <ShieldCheck size={16} className="text-red-600 mt-0.5 shrink-0" />
                  <span>Your account is suspended, so you can't sign in. Tell our team why you believe this was a mistake and we'll review your appeal.</span>
                </div>
              )}
              <div>
                <Label htmlFor="appeal-message">Your appeal</Label>
                <Textarea id="appeal-message" data-testid="appeal-message" value={appealMsg} onChange={(e) => setAppealMsg(e.target.value)} rows={5} maxLength={2000}
                  className="mt-1 rounded-xl resize-none" placeholder="Explain your situation and why your account should be reinstated…" autoFocus />
              </div>
              <Button type="submit" disabled={loading || appealMsg.trim().length < 10} data-testid="appeal-submit" className="w-full h-11 rounded-xl bg-[#12234A] hover:bg-[#0E1B38] text-white font-semibold">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Submit appeal"}
              </Button>
              <button type="button" onClick={closeAppeal} data-testid="appeal-cancel" className="w-full text-center text-sm text-slate-400 hover:text-slate-600">
                Back to sign in
              </button>
            </form>
          )
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={googleLogin}
              data-testid="google-login-btn"
              className="w-full gap-2 h-11 rounded-xl border-slate-300"
            >
              <GoogleIcon /> Continue with Google
            </Button>

            <div className="flex items-center gap-3 text-xs text-slate-400">
              <div className="flex-1 h-px bg-slate-200" /> or <div className="flex-1 h-px bg-slate-200" />
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "register" && (
                <div>
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" data-testid="auth-name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1 h-11 rounded-xl" placeholder="Jane Doe" />
                </div>
              )}
              <div>
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="auth-email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1 h-11 rounded-xl" placeholder="you@example.com" />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" data-testid="auth-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1 h-11 rounded-xl" placeholder="••••••••" />
              </div>
              {mode === "register" && (
                <label className="flex items-start gap-2 text-xs text-slate-500 cursor-pointer" data-testid="auth-newsletter-optin">
                  <input type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} data-testid="auth-newsletter-checkbox" className="mt-0.5 h-4 w-4 accent-[#F5551E]" />
                  <span>{lang === "fr" ? "Envoyez-moi l'infolettre — nouvelles, conseils et nouveaux animaux. Désabonnement en tout temps." : "Send me the newsletter — news, tips and new pets. Unsubscribe any time."}</span>
                </label>
              )}
              {mode === "register" && (
                <label className="flex items-start gap-2 text-xs text-slate-500 cursor-pointer" data-testid="auth-seasonal-optin">
                  <input type="checkbox" checked={seasonal} onChange={(e) => setSeasonal(e.target.checked)} data-testid="auth-seasonal-checkbox" className="mt-0.5 h-4 w-4 accent-[#F5551E]" />
                  <span>{t("se_signup")}</span>
                </label>
              )}
              <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full h-11 rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold">
                {loading ? <Loader2 className="animate-spin" size={18} /> : mode === "login" ? "Sign in" : "Create account"}
              </Button>
              {mode === "login" && (
                <button type="button" onClick={() => { setAuthOpen(false); navigate("/forgot-password"); }} data-testid="forgot-password-link" className="w-full text-center text-sm text-slate-400 hover:text-[#F5551E]">
                  Forgot your password?
                </button>
              )}
            </form>

            <div className="flex items-center justify-between text-sm">
              <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="text-[#F5551E] font-medium" data-testid="auth-toggle-mode">
                {mode === "login" ? "New here? Create an account" : "Have an account? Sign in"}
              </button>
              <button onClick={demo} className="text-slate-400 hover:text-slate-600" data-testid="auth-demo-fill">Use demo</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
